import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { threadApi } from "@/services/threadApi/threadApi";
import { updateMessageDataVisualization } from "@/store/chat/chat.slice";
import { RootState } from "@/store/store";
import { resetActiveThread } from "@/store/thread/threadSlice";

import { TInitKpiDataVisualizationPayload, TKPIDataVisualizationState, TSyncKPIDataVisualizationFromServerPayload, TUpdateKpiDataVisualizationPayload } from "./types";
import { applyKpiVisualizationUpdate } from "./utils";

const initialState: TKPIDataVisualizationState = { entities: {} };

// INFO: This slice is used to hold UI state changes, it is the single sort of truth for KPI data visualization information
// We need to add this layer in order to prevent local state override when new messages are sent
// TODO: As a future improvement we should prevent the sendMessage mutation to rebuild the entire messages list, then this slice would be unnecessary
const KPIDataVisualizationPrefernceSlice = createSlice({
  name: 'kpiDataVisualizationPreference',
  initialState,
  reducers: {
    initKPIDataVisualization(state, action: PayloadAction<TInitKpiDataVisualizationPayload>) {
      const { messageId, data: mutationData } = action.payload;

      if (state.entities[messageId]) return;

      state.entities[messageId] = { data: mutationData, savedData: mutationData, isDirty: false };
    },
    updateKPIDataVisualization(state, action: PayloadAction<TUpdateKpiDataVisualizationPayload>) {
      applyKpiVisualizationUpdate(state, action.payload)
    },

    syncKPIDataVisualizationFromServer(state, action: PayloadAction<TSyncKPIDataVisualizationFromServerPayload>) {
      const { messageId, data: mutationData } = action.payload;

      // TOODO: TEMP - FIBOT-1396
      const merged = {
        ...state.entities[messageId]?.data,
        ...mutationData,
      };

      state.entities[messageId] = {
        data: merged,
        savedData: merged,
        isDirty: false,
      };  
    },
    // INFO: In the future we might need to reset the data visualizzation to the last saved data
    resetKPIDataVisualization(state, action: PayloadAction<{ messageId: number }>) {
      const entry = state.entities[action.payload.messageId];

      if (!entry) return;

      entry.data = entry.savedData;
      entry.isDirty = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(resetActiveThread, () => initialState);
    if(updateMessageDataVisualization) { // INFO: Prevents case to be added before chat.slice is initilised in prod/test envs
        builder.addCase(updateMessageDataVisualization, (state, action) => {
        // INFO: Updates visualization mode between table <-> graph and keeps the UI data consistent
        const { messageId, data_visualization } = action.payload;
        applyKpiVisualizationUpdate(state, {
          messageId: messageId,
          changes: {
            default_data_visualization: data_visualization.default_data_visualization,
            x_axis: data_visualization.x_axis
          }
        })
      })
    }
    builder.addMatcher(threadApi.endpoints.fetchMessagesByThreadId.matchFulfilled, () => initialState);
  },
});

export const {
  initKPIDataVisualization,
  updateKPIDataVisualization,
  syncKPIDataVisualizationFromServer,
  resetKPIDataVisualization
} = KPIDataVisualizationPrefernceSlice.actions;

export default KPIDataVisualizationPrefernceSlice.reducer;

// #region Selectors

export const selectKPIDataVisualization = (messageId: string) => (state: RootState) => state.kpiDataVisualizationPreference.entities[messageId];

export const selectIsKPIDirty = (messageId: string) => (state: RootState) => state.kpiDataVisualizationPreference.entities[messageId]?.isDirty ?? false;

// #endregion Selectors
